import React, { useState, useEffect, useRef } from 'react';
import './AdminTermsPanel.css';
import api from '../services/api';

const AdminTermsPanel = () => {
  const [terms, setTerms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // list, create, edit, notify, stats
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [message, setMessage] = useState('');

  const [editBase, setEditBase] = useState(null); // term loaded for edit with full translations
  const [formData, setFormData] = useState({
    version: '',
    effective_date: new Date().toISOString().split('T')[0],
    is_active: false,
    translations: {
      sk: { title: '', content: '' },
      en: { title: '', content: '' }
    }
  });


    // Refs for inserting tokens at cursor
    const skCreateRef = useRef(null);
    const enCreateRef = useRef(null);
    const skEditRef = useRef(null);
    const enEditRef = useRef(null);

    const tokenList = [
      { label: 'Meno používateľa', token: '{{user.full_name}}' },
      { label: 'Adresa', token: '{{user.address}}' },
      { label: 'IČO', token: '{{user.ico}}' },
      { label: 'DIČ', token: '{{user.dic}}' },
    ];

    const insertAtCursor = (lang, token) => {
      const isEdit = activeTab === 'edit';
      const ref = isEdit
        ? (lang === 'sk' ? skEditRef.current : enEditRef.current)
        : (lang === 'sk' ? skCreateRef.current : enCreateRef.current);
      const current = (formData.translations?.[lang]?.content) || '';
      try {
        if (ref && typeof ref.selectionStart === 'number') {
          const start = ref.selectionStart;
          const end = ref.selectionEnd;
          const next = current.slice(0, start) + token + current.slice(end);
          setFormData({
            ...formData,
            translations: {
              ...formData.translations,
              [lang]: { ...formData.translations[lang], content: next }
            }
          });
          setTimeout(() => { try { ref.focus(); ref.setSelectionRange(start + token.length, start + token.length); } catch(_) {} }, 0);
        } else {
          const next = current + token;
          setFormData({
            ...formData,
            translations: {
              ...formData.translations,
              [lang]: { ...formData.translations[lang], content: next }
            }
          });
        }
      } catch (e) {
        const next = current + token;
        setFormData({
          ...formData,
          translations: {
            ...formData.translations,
            [lang]: { ...formData.translations[lang], content: next }
          }
        });
      }
    };

    const TokenToolbar = ({ lang }) => (
      <div className="atp-token-toolbar" style={{ margin: '6px 0' }}>
        <small style={{ marginRight: 8 }}>Vložiť premennú:</small>
        {tokenList.map((t, idx) => (
          <button type="button" key={idx} className="atp-btn atp-btn-small" style={{ marginRight: 6 }} onClick={() => insertAtCursor(lang, t.token)}>
            {t.label}
          </button>
        ))}
      </div>
    );

  useEffect(() => {
    fetchTerms();
    fetchUsers();
  }, []);

  const parseVersion = (v) => {
    if (!v) return { major: 1, minor: 0 };
    const m = String(v).trim().match(/^(\d+)(?:\.(\d+))?$/);
    if (!m) return { major: 1, minor: 0 };
    return { major: parseInt(m[1], 10), minor: parseInt(m[2] || '0', 10) };
  };
  const nextMinor = (v) => {
    const { major, minor } = parseVersion(v);
    return `${major}.${minor + 1}`;
  };
  const nextMajor = (v) => {
    const { major } = parseVersion(v);
    return `${major + 1}.0`;
  };

  const fetchTerms = async () => {
    try {
      const { data } = await api.get('/admin/terms');
      setTerms(data.terms || []);
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateTerm = async (e) => {
    e.preventDefault();
    
    if (!formData.version || !formData.translations.sk.title || !formData.translations.sk.content) {
      setMessage('Please fill in version and Slovak translation (required)');
      return;
    }

    try {
      await api.post('/admin/terms', formData);
      setMessage('Terms created successfully!');
      fetchTerms();
      setActiveTab('list');
      resetForm();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const handleActivateTerm = async (termId) => {
    try {
      await api.post(`/admin/terms/${termId}/activate`);
      setMessage('Terms activated successfully!');
      fetchTerms();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  

  const handleDeleteTerm = async (term) => {
    try {
      if (term.is_active) {
        setMessage('Cannot delete an active terms version');
        return;
      }
      if (typeof term.acceptance_count === 'number' && term.acceptance_count > 0) {
        setMessage('Cannot delete a version that has user acceptances');
        return;
      }
      if (!window.confirm(`Delete terms version ${term.version}? This cannot be undone.`)) return;
      await api.delete(`/admin/terms/${term.id}`);
      setMessage(`Terms v${term.version} deleted`);
      fetchTerms();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || 'Unknown error';
      setMessage(`Error deleting terms: ${msg}`);
    }
  };
const handleNotifyUsers = async () => {
    if (selectedUsers.length === 0) {
      setMessage('Please select at least one user');
      return;
    }

    try {
      const { data } = await api.post(`/admin/terms/${selectedTerm.id}/notify`, {
        userIds: selectedUsers,
        sendEmail
      });
      setMessage(data.message);
      setSelectedUsers([]);
      setSendEmail(false);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const handleViewStats = async (termId) => {
    try {
      const { data } = await api.get(`/admin/terms/${termId}/stats`);
      setSelectedTerm({ ...terms.find(t => t.id === termId), stats: data.stats });
      setActiveTab('stats');
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      version: '',
      effective_date: new Date().toISOString().split('T')[0],
      is_active: false,
      translations: {
        sk: { title: '', content: '' },
        en: { title: '', content: '' }
      }
    });
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  if (loading) {
    return <div className="atp-loading">Loading...</div>;
  }

  return (
    <div className="atp-admin-terms-panel">
      <h1>📋 Terms & Conditions Management</h1>

      {message && (
        <div className="atp-admin-message">
          {message}
          <button onClick={() => setMessage('')}>×</button>
        </div>
      )}

      <div className="atp-admin-tabs">
        <button 
          className={activeTab === 'list' ? 'atp-active' : ''}
          onClick={() => setActiveTab('list')}
        >
          📄 Terms List
        </button>
        <button 
          className={activeTab === 'create' ? 'atp-active' : ''}
          onClick={() => { setActiveTab('create'); resetForm(); }}
        >
          ➕ Create New
        </button>
        <button 
          className={activeTab === 'notify' ? 'atp-active' : ''}
          onClick={() => setActiveTab('notify')}
        >
          📧 Notify Users
        </button>

        {activeTab === 'stats' && (
          <button className="atp-active">
            📊 Statistics
          </button>
        )}
      </div>

      {/* TERMS LIST */}
      {activeTab === 'list' && (
        <div className="atp-terms-list">
          <table className="atp-terms-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Languages</th>
                <th>Effective Date</th>
                <th>Status</th>
                <th>Acceptances</th>
                <th>Notifications</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {terms.map(term => (
                <tr key={term.id}>
                  <td><strong>{term.version}</strong></td>
                  <td>
                    {term.translations?.map(t => (
                      <span key={t.language} className="atp-language-badge">{t.language.toUpperCase()}</span>
                    ))}
                  </td>
                  <td>{new Date(term.effective_date).toLocaleDateString()}</td>
                  <td>
                    {term.is_active ? (
                      <span className="atp-status-badge atp-active">Active</span>
                    ) : (
                      <span className="atp-status-badge">Inactive</span>
                    )}
                  </td>
                  <td>{term.acceptance_count}</td>
                  <td>{term.notification_count}</td>
                  <td>
                    {!term.is_active && (
                      <button
                        className="atp-btn atp-btn-small atp-btn-primary"
                        onClick={() => handleActivateTerm(term.id)}
                      >
                        Activate
                      </button>
                    )}
                    <button
                      className="atp-btn atp-btn-small"
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/admin/terms/${term.id}`);
                          const base = data.term;
                          const translations = { sk: { title: '', content: '' }, en: { title: '', content: '' } };
                          (base.translations || []).forEach(t => {
                            const lang = (t.language || '').toLowerCase();
                            if (!translations[lang]) translations[lang] = { title: '', content: '' };
                            translations[lang].title = t.title || '';
                            translations[lang].content = t.content || '';
                          });
                          // Fallback pre staršie verzie: ak chýba SK v translations, použij hlavnú tabuľku
                          if (!translations.sk.title && base.title) translations.sk.title = base.title;
                          if (!translations.sk.content && base.content) translations.sk.content = base.content;

                          setEditBase(base);
                          setFormData({
                            version: nextMinor(base.version),
                            effective_date: (base.effective_date || new Date().toISOString().split('T')[0]).split('T')[0],
                            is_active: false,
                            translations
                          });
                          setActiveTab('edit');
                        } catch (e) {
                          setMessage(`Error loading term: ${e.message}`);
                        }
                      }}
                    >
                      ✏️ Edit (minor)
                    </button>
                    <button
                      className="atp-btn atp-btn-small"
                      onClick={() => handleViewStats(term.id)}
                    >
                      Stats
                    </button>
                    <button
                      className="atp-btn atp-btn-small atp-btn-danger"
                      onClick={() => handleDeleteTerm(term)}
                      disabled={term.is_active || (typeof term.acceptance_count === 'number' && term.acceptance_count > 0)}
                    >
                      🗑 Delete
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CREATE FORM */}
      {activeTab === 'create' && (
        <div className="atp-terms-form">
          <form onSubmit={handleCreateTerm}>
            <div className="atp-form-group">
              <label>Version *</label>
              <div className="atp-inline">
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                  placeholder="e.g., 2.0"
                  required
                />
                <button type="button" className="atp-btn atp-btn-small"
                  onClick={() => {
                    const latest = terms && terms.length ? terms[0] : null;
                    const v = nextMajor(latest?.version || '0.0');
                    setFormData({ ...formData, version: v });
                  }}
                >
                  Suggest next major ({nextMajor(terms && terms.length ? terms[0].version : '0.0')})
                </button>
              </div>
            </div>

            <div className="atp-form-group">
              <label>Effective Date</label>
              <input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
              />
            </div>

            <div className="atp-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Set as Active
              </label>
            </div>

            <div className="atp-translation-editors">
              <h3>🇸🇰 Slovak Translation (Required)</h3>
              <div className="atp-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.translations.sk.title}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      sk: { ...formData.translations.sk, title: e.target.value }
                    }
                  })}
                  placeholder="Obchodné podmienky"
                  required
                />
              </div>
              <div className="atp-form-group">
                <label>Content (Markdown) *</label>
                <TokenToolbar lang="sk" />
                <textarea
                  ref={skCreateRef}
                  value={formData.translations.sk.content}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      sk: { ...formData.translations.sk, content: e.target.value }
                    }
                  })}
                  rows="10"
                  placeholder="# Heading\n\n## Subheading\n\n- List item\n\n**Bold text**"
                  required
                />
              </div>

              <h3>🇬🇧 English Translation (Optional)</h3>
              <div className="atp-form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.translations.en.title}
                  onChange={(e) => setFormData({
                    ...formData,
      
                    translations: {
                      ...formData.translations,
                      en: { ...formData.translations.en, title: e.target.value }
                    }
                  })}
                  placeholder="Terms and Conditions"
                />
              </div>
              <div className="atp-form-group">
                <label>Content (Markdown)</label>
                <TokenToolbar lang="en" />
                <textarea
                  ref={enCreateRef}
                  value={formData.translations.en.content}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      en: { ...formData.translations.en, content: e.target.value }
                    }
                  })}
                  rows="10"
                  placeholder="# Heading\n\n## Subheading\n\n- List item\n\n**Bold text**"
                />
              </div>
            </div>

            <div className="atp-form-actions">
              <button type="submit" className="atp-btn atp-btn-primary">Create Terms</button>
              <button type="button" onClick={() => setActiveTab('list')}>Cancel</button>
            </div>
          </form>
        </div>
      )}

{/* EDIT FORM (minor version) */}
      {activeTab === 'edit' && editBase && (
        <div className="atp-terms-form">
          <h2>Edit Terms (base v{editBase.version}) → New v{formData.version}</h2>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!formData.translations?.sk?.title || !formData.translations?.sk?.content) {
              setMessage('Please fill in Slovak translation (title + content)');
              return;
            }
            try {
              // Create new minor version as a brand new record
              const payload = { ...formData };
              if (!payload.version) payload.version = nextMinor(editBase.version);
              await api.post('/admin/terms', payload);
              setMessage(`Terms v${payload.version} created successfully`);
              setActiveTab('list');
              setEditBase(null);
              resetForm();
              fetchTerms();
            } catch (error) {
              setMessage(`Error: ${error.message}`);
            }
          }}>
            <div className="atp-form-group">
              <label>New Version (minor) *</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder={`${nextMinor(editBase.version)}`}
                required
              />
            </div>
            <div className="atp-form-group">
              <label>Effective Date</label>
              <input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
              />
            </div>
            <div className="atp-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Set as Active
              </label>
            </div>

            <div className="atp-translation-editors">
              <h3>🇸🇰 Slovak Translation (Required)</h3>
              <div className="atp-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.translations.sk.title}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      sk: { ...formData.translations.sk, title: e.target.value }
                    }
                  })}
                  required
                />
              </div>
              <div className="atp-form-group">
                <label>Content (Markdown) *</label>
                <TokenToolbar lang="sk" />
                <textarea
                  ref={skEditRef}
                  rows="10"
                  value={formData.translations.sk.content}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      sk: { ...formData.translations.sk, content: e.target.value }
                    }
                  })}
                  required
                />
              </div>
              <h3>🇬🇧 English Translation (Optional)</h3>
              <div className="atp-form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.translations.en.title}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      en: { ...formData.translations.en, title: e.target.value }
                    }
                  })}
                />
              </div>
              <div className="atp-form-group">
                <label>Content (Markdown)</label>
                <TokenToolbar lang="en" />
                <textarea
                  ref={enEditRef}
                  rows="10"
                  value={formData.translations.en.content}
                  onChange={(e) => setFormData({
                    ...formData,
                    translations: {
                      ...formData.translations,
                      en: { ...formData.translations.en, content: e.target.value }
                    }
                  })}
                />
              </div>
            </div>

            <div className="atp-form-actions">
              <button type="submit" className="atp-btn atp-btn-primary">💾 Save as v{formData.version}</button>
              <button type="button" className="atp-btn"
                onClick={() => {
                  // Quick helper to bump to next major
                  const v = nextMajor(editBase.version);
                  setFormData({ ...formData, version: v });
                }}
              >
                ⤴️ Suggest next major ({nextMajor(editBase.version)})
              </button>
              <button type="button" onClick={() => { setActiveTab('list'); setEditBase(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* NOTIFY USERS */}
      {activeTab === 'notify' && (
        <div className="notify-section">
          <h2>Send Notifications</h2>
          
          <div className="atp-form-group">
            <label>Select Terms Version</label>
            <select 
              onChange={(e) => setSelectedTerm(terms.find(t => t.id === parseInt(e.target.value)))}
              value={selectedTerm?.id || ''}
            >
              <option value="">-- Select --</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>
                  Version {term.version} {term.is_active ? '(Active)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedTerm && (
            <>
              <div className="user-selection">
                <div className="selection-header">
                  <h3>Select Users ({selectedUsers.length} selected)</h3>
                  <div>
                    <button onClick={selectAllUsers} className="atp-btn atp-btn-small">Select All</button>
                    <button onClick={deselectAllUsers} className="atp-btn atp-btn-small">Deselect All</button>
                  </div>
                </div>

                <div className="atp-users-grid">
                  {users.map(user => (
                    <label key={user.id} className="user-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserSelection(user.id)}
                      />
                      <span className="atp-user-info">
                        <strong>{user.username}</strong>
                        <small>{user.email || 'No email'}</small>
                        {user.terms_accepted_version && (
                          <small className="version-badge">v{user.terms_accepted_version}</small>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="atp-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                  Send Email Notifications
                </label>
              </div>

              <div className="atp-form-actions">
                <button 
                  onClick={handleNotifyUsers} 
                  className="atp-btn atp-btn-primary"
                  disabled={selectedUsers.length === 0}
                >
                  📧 Notify {selectedUsers.length} User(s)
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* STATISTICS */}
      {activeTab === 'stats' && selectedTerm?.stats && (
        <div className="atp-stats-section">
          <h2>Statistics for Version {selectedTerm.version}</h2>
          
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <h3>{selectedTerm.stats.acceptances?.length || 0}</h3>
              <p>Total Acceptances</p>
            </div>
            <div className="admin-stat-card">
              <h3>{selectedTerm.stats.notifications?.length || 0}</h3>
              <p>Notified Users</p>
            </div>
            <div className="admin-stat-card">
              <h3>{selectedTerm.stats.pending?.length || 0}</h3>
              <p>Pending Acceptances</p>
            </div>
          </div>

          <div className="stats-tables">
            <div>
              <h3>Recent Acceptances</h3>
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Accepted At</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTerm.stats.acceptances?.slice(0, 10).map((acc, idx) => (
                    <tr key={idx}>
                      <td>{acc.username}</td>
                      <td>{acc.email || '-'}</td>
                      <td>{new Date(acc.agreed_at).toLocaleString()}</td>
                      <td><code>{acc.ip_address}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedTerm.stats.pending?.length > 0 && (
              <div>
                <h3>Pending Acceptances</h3>
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Notified At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTerm.stats.pending.map((pend, idx) => (
                      <tr key={idx}>
                        <td>{pend.username}</td>
                        <td>{pend.email || '-'}</td>
                        <td>{new Date(pend.notified_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <button onClick={() => setActiveTab('list')} className="btn-back">
            ← Back to List
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTermsPanel;
